import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  Renderer2,
  SkipSelf,
  ViewChild,
} from '@angular/core';

import { Observable, of as observableOf, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { UserBase } from '@recruitee/http/types';

import { AvatarThemeSubject } from '../avatar/avatar-theme.directive';
import { AvatarShape, AvatarSize } from '../avatar/avatar.types';
import { TooltipPosition } from '../tooltip/tooltip-inner.component';
import { ComponentVariant, Variants } from '../variant/variant';

export function stackedAvatarThemeSubject(
  themeSubject?: AvatarThemeSubject,
): Observable<string | null> {
  return themeSubject || observableOf('background-default');
}

type UserWithPresence = UserBase & { isOnline?: boolean };

@Component({
  selector: 'rt-admins-avatars',
  templateUrl: './admins-avatars.component.html',
  styleUrls: ['./admins-avatars.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.horizontal]': 'direction === "horizontal"',
    '[class.vertical]': 'direction === "vertical"',
    '[class.spacing--small]': 'spacing === "small"',
    '[class.spacing--small-x]': 'spacing === "small-x"',
    '[class.is-short]': 'isShort',
    '[class]': 'variant',
  },
  providers: [
    {
      provide: AvatarThemeSubject,
      useFactory: stackedAvatarThemeSubject,
      deps: [[new Optional(), new SkipSelf(), AvatarThemeSubject]],
    },
  ],
})
export class AdminsAvatarsComponent {
  public static readonly VARIANTS: Variants = ['ltr', ['default', 'ltr', 'rtl']];
  @ComponentVariant(AdminsAvatarsComponent.VARIANTS)
  @Input()
  public variant: string;
  @Input() public admins: UserWithPresence[] = [];
  @Input() public avatarSize: AvatarSize = 'small';
  @Input() public avatarShape: AvatarShape = 'oval';
  @Input() public editable: boolean = false;
  @Input() public stacked: boolean = false;
  @Input() public isRole: boolean = false;
  @Input() public hasStopPropagation: boolean = true;
  @Input() public limit?: number;
  @Input() public direction: 'horizontal' | 'vertical' = 'horizontal';
  @Input() public spacing: 'small' | 'small-x' = 'small-x';
  @Input('tooltipPosition') public _tooltipPosition?: TooltipPosition;
  @Input() public tooltipMaxWidth?: number | null;
  @Input() public tooltipWordWrap: boolean = true;
  @Input() public renderAdminTooltip?: (admin: any) => string;
  @Input() public renderOtherAdminsTooltip?: (admins: any[]) => string;
  @Input() public themeContext: 'default' | 'overlay' = 'default'; // FIXME: is this used anywhere?
  @Input() public multiline: boolean = false;
  @Input() public isShort: boolean = false;
  @Input() public analyticsEvent?: string;

  @Output() public avatarsClick: EventEmitter<void> = new EventEmitter();
  @Output() public addClick: EventEmitter<void> = new EventEmitter();
  @Output() public moreClick: EventEmitter<void> = new EventEmitter();

  @ViewChild('moreButton', { read: ElementRef })
  public moreButton: ElementRef;

  public otherAdmins: UserWithPresence[] = [];

  private ngOnDestroy$ = new Subject<void>();
  private currentThemeClass: string;
  private newThemeClass: string;

  constructor(private renderer2: Renderer2, @Optional() private themeClass$: AvatarThemeSubject) {}

  public get tooltipPosition(): TooltipPosition {
    return this._tooltipPosition || (this.otherAdmins.length > 2 ? 'below' : 'above');
  }

  public ngOnChanges(): void {
    this.otherAdmins = this.admins.slice(this.limit || 0, this.admins.length);
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public ngAfterViewInit(): void {
    if (this.themeClass$) {
      this.themeClass$.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(themeClass => {
        if (this.moreButton) {
          if (this.currentThemeClass) {
            this.renderer2.removeClass(this.moreButton.nativeElement, this.currentThemeClass);
          }

          this.renderer2.addClass(this.moreButton.nativeElement, this.newThemeClass!);
          this.currentThemeClass = this.newThemeClass!;
        }
      });
    }
  }

  public visibleAssignments(): UserWithPresence[] {
    return this.hasMore() ? this.admins.slice(0, this.limit || 0) : this.admins;
  }

  public trackByAdmin(index: number, admin: UserWithPresence): number {
    return admin.id;
  }

  public hasMore(): boolean {
    return this.limit ? this.admins.length > this.limit : false;
  }

  public onAddClick(): void {
    if (this.editable) {
      this.addClick.emit();
    }
  }
  public onAvatarsClick(): void {
    if (this.editable) {
      this.avatarsClick.emit();
    }
  }

  public stopPropagation(event: MouseEvent): void {
    if (this.hasStopPropagation) {
      event.stopPropagation();
    }
  }
}
